/**
 * On start, params are stored from GET parameters.
 * Application is also stored for display and verification information.
 */
import Action from 'lib/interfaces/Action';
import { SelectedCamera } from 'verify/scanners/GovernmentIdMicroblinkScanner/MicroblinkCameraUtils';

export enum UiActionType {
  SetStatus = 'ui/SET_STATUS',
  SetConfirmCancelDialog = 'ui/SET_CONFIRM_CANCEL_DIALOG',
  SetHasCamera = 'ui/SET_HAS_CAMERA',
  SetCameraOptions = 'ui/SET_CAMERA_OPTIONS',
  IncrementPollerId = 'ui/INCREMENT_POLLER_ID',
}

export enum UiStatus {
  /**
   * Inquiry flow is loading for the first time
   */
  Initializing = 'initializing',
  /**
   * UI is ready for interaction
   */
  Ready = 'ready',
  /**
   * UI is waiting for a network response
   */
  Transitioning = 'transitioning',
  /**
   * UI is in a polling loop. Back and close buttons should be disabled.
   */
  Blocked = 'blocked',
}

interface SetStatusPayload {
  status: UiStatus;
}

export function setTransitionStart(): Action<UiActionType.SetStatus, SetStatusPayload> {
  return {
    type: UiActionType.SetStatus,
    payload: {
      status: UiStatus.Transitioning,
    },
  };
}

export function setUiBlocked(): Action<UiActionType.SetStatus, SetStatusPayload> {
  return {
    type: UiActionType.SetStatus,
    payload: {
      status: UiStatus.Blocked,
    },
  };
}

export function setUiReady(): Action<UiActionType.SetStatus, SetStatusPayload> {
  return {
    type: UiActionType.SetStatus,
    payload: {
      status: UiStatus.Ready,
    },
  };
}

export function incrementPollerId(): Action<UiActionType.IncrementPollerId> {
  return {
    type: UiActionType.IncrementPollerId,
  };
}

interface SetConfirmCancelDialogPayload {
  show: boolean;
}

export function openConfirmCancelDialog(): Action<
  UiActionType.SetConfirmCancelDialog,
  SetConfirmCancelDialogPayload
> {
  return {
    type: UiActionType.SetConfirmCancelDialog,
    payload: {
      show: true,
    },
  };
}

export function closeConfirmCancelDialog(): Action<
  UiActionType.SetConfirmCancelDialog,
  SetConfirmCancelDialogPayload
> {
  return {
    type: UiActionType.SetConfirmCancelDialog,
    payload: {
      show: false,
    },
  };
}

interface SetHasCameraPayload {
  hasCamera: boolean;
}

export function setHasCamera(
  hasCamera: boolean,
): Action<UiActionType.SetHasCamera, SetHasCameraPayload> {
  return {
    type: UiActionType.SetHasCamera,
    payload: {
      hasCamera,
    },
  };
}

export interface CameraOptions {
  frontCameras: SelectedCamera[];
  backCameras: SelectedCamera[];
}

interface SetCameraOptionsPayload {
  cameraOptions: CameraOptions;
}

export function setCameraOptions(
  cameraOptions: CameraOptions,
): Action<UiActionType.SetCameraOptions, SetCameraOptionsPayload> {
  return {
    type: UiActionType.SetCameraOptions,
    payload: {
      cameraOptions,
    },
  };
}

export type UiActions =
  | Action<UiActionType.SetStatus, SetStatusPayload>
  | Action<UiActionType.SetConfirmCancelDialog, SetConfirmCancelDialogPayload>
  | Action<UiActionType.SetHasCamera, SetHasCameraPayload>
  | Action<UiActionType.SetCameraOptions, SetCameraOptionsPayload>
  | Action<UiActionType.IncrementPollerId>;
